import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LangContext";
import { useGuest } from "@/contexts/GuestContext";
import MotionReveal from "@/components/MotionReveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PencilLine, Search } from "lucide-react";
import api from "@/lib/api";
import MenuPreorder from "@/components/MenuPreorder";
import { buildPreorderPayload, summarisePreorder, itemsToCounts } from "@/data/menu";

const init = {
  name: "",
  email: "",
  attending: "yes",
  guest_count: 1,
  kid_count: 0,
  accommodation: "no",
  arrival_date: "",
  departure_date: "",
  allergies: "",
  song_request: "",
  message: "",
};

const rsvpToForm = (r) => ({
  name: r.name || "",
  email: r.email || "",
  attending: r.attending || "yes",
  guest_count: r.guest_count || 1,
  kid_count: r.kid_count || 0,
  accommodation: r.transportation || "no",
  arrival_date: r.arrival_date || "",
  departure_date: r.departure_date || "",
  allergies: r.allergies || "",
  song_request: r.song_request || "",
  message: r.message || "",
});

const Rsvp = () => {
  const { t, lang } = useLang();
  const { guestRsvp, refresh: refreshGuest, lookupByName } = useGuest();
  const [form, setForm] = useState(init);
  const [menuCounts, setMenuCounts] = useState({});
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showLookup, setShowLookup] = useState(false);
  const [lookupName, setLookupName] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);

  const editMode = !!editingId;

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Prefill from magic-link RSVP whenever the guest lands here with one on file.
  useEffect(() => {
    if (!guestRsvp || editingId === guestRsvp.id) return;
    setForm(rsvpToForm(guestRsvp));
    setMenuCounts(itemsToCounts(guestRsvp.preorder || []));
    setEditingId(guestRsvp.id);
  }, [guestRsvp, editingId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t("rsvp.errorRequired") || "Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      const preorder = buildPreorderPayload(menuCounts);
      const payload = {
        name: form.name,
        email: form.email,
        attending: form.attending,
        guest_count: Number(form.guest_count) || 1,
        kid_count: Number(form.kid_count) || 0,
        arrival_date: form.arrival_date || "",
        departure_date: form.departure_date || "",
        dietary: "",
        allergies: form.allergies,
        transportation: form.accommodation,
        song_request: form.song_request,
        message: form.message,
        language: lang,
        preorder,
      };
      let res;
      if (editingId) {
        res = await api.put(`/rsvp/${editingId}`, payload);
      } else {
        res = await api.post("/rsvp", payload);
      }
      try {
        const first = (form.name || "").trim().split(/\s+/)[0];
        if (first) localStorage.setItem("em_guest_name", first);
      } catch { /* ignore */ }
      setSubmitted({ ...res.data, accommodation: form.accommodation, preorder, wasEdit: !!editingId });
      // Refresh context so the personalised hero updates when they navigate home.
      refreshGuest();
      toast.success(editingId ? (t("rsvp.updatedTitle") || "Your reply was updated") : t("rsvp.successTitle"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error(t("rsvp.errorNameMismatch") || "Name doesn't match your original reply. Type it exactly as you sent it.");
      } else {
        toast.error(t("rsvp.errorGeneric"));
      }
    } finally {
      setBusy(false);
    }
  };

  const onFindMyReply = async () => {
    const clean = (lookupName || "").trim();
    if (!clean) return;
    setLookupBusy(true);
    try {
      const found = await lookupByName(clean);
      if (found && found.id) {
        setForm(rsvpToForm(found));
        setMenuCounts(itemsToCounts(found.preorder || []));
        setEditingId(found.id);
        setShowLookup(false);
        setLookupName("");
        toast.success(t("rsvp.foundToast") || "Found your reply — you can edit anything below.");
        window.scrollTo({ top: 240, behavior: "smooth" });
      } else {
        toast.error(t("rsvp.notFoundToast") || "No reply found under that name. Check the spelling or start a fresh one.");
      }
    } finally {
      setLookupBusy(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(init);
    setMenuCounts({});
    toast(t("rsvp.editCancelledToast") || "Editing cancelled — form is fresh.");
  };

  if (submitted) {
    return (
      <div className="mx-auto max-w-[1180px] px-5 sm:px-8 pt-24 sm:pt-32 pb-24" data-testid="rsvp-page">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          className="max-w-[640px]"
          data-testid="rsvp-form-success-state"
        >
          <p className="editorial-kicker text-accent">{t("rsvp.overline")}</p>
          <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl">
            {submitted.wasEdit ? (t("rsvp.updatedTitle") || "Reply updated") : t("rsvp.successTitle")}
          </h1>
          <p className="mt-6 text-foreground/75 leading-relaxed max-w-[44ch]">
            {submitted.wasEdit ? (t("rsvp.updatedBody") || "Your changes are saved. Andreas noted them, siga siga.") : t("rsvp.successBody")}
          </p>
          <div className="mt-10 surface-card p-6">
            <p className="editorial-kicker">{t("rsvp.summary") || "Summary"}</p>
            <dl className="mt-4 grid grid-cols-3 gap-y-2 text-sm">
              <dt className="text-foreground/55">{t("rsvp.fields.name")}</dt>
              <dd className="col-span-2">{submitted.name}</dd>
              <dt className="text-foreground/55">{t("rsvp.fields.email")}</dt>
              <dd className="col-span-2">{submitted.email}</dd>
              <dt className="text-foreground/55">{t("rsvp.fields.attending")}</dt>
              <dd className="col-span-2">{submitted.attending === "yes" ? t("rsvp.fields.attendingYes") : t("rsvp.fields.attendingNo")}</dd>
              <dt className="text-foreground/55">{t("rsvp.fields.guests")}</dt>
              <dd className="col-span-2">{submitted.guest_count}</dd>
              {submitted.preorder && submitted.preorder.length > 0 && (
                <>
                  <dt className="text-foreground/55">{t("rsvp.menu.overline")}</dt>
                  <dd className="col-span-2" data-testid="rsvp-success-preorder">
                    {summarisePreorder(submitted.preorder)}
                  </dd>
                </>
              )}
            </dl>
          </div>
          <button
            type="button"
            onClick={() => {
              setSubmitted(null);
              // Keep the ID so they can edit again if they want; but reset the flag.
              if (submitted.wasEdit) {
                // Stay in edit mode with fresh data from server
                setForm(rsvpToForm(submitted));
                setMenuCounts(itemsToCounts(submitted.preorder || []));
              } else {
                setForm(init);
                setMenuCounts({});
                setEditingId(null);
              }
            }}
            data-testid="rsvp-send-another"
            className="mt-8 text-[12px] tracking-[0.22em] uppercase border-b border-foreground/40 hover:text-accent hover:border-accent transition-colors"
          >
            {submitted.wasEdit ? (t("rsvp.editAgain") || "Edit again") : t("rsvp.successAnother")}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="rsvp-page">
      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 pt-24 sm:pt-32">
        <MotionReveal>
          <p className="editorial-kicker">
            {editMode ? (t("rsvp.editKicker") || "Edit your reply") : t("rsvp.overline")}
          </p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl lg:text-7xl max-w-[20ch]">
            {editMode ? (t("rsvp.editTitle") || "Change of plan?") : t("rsvp.title")}
          </h1>
        </MotionReveal>
        <MotionReveal delay={0.1}>
          <p className="editorial-kicker mt-6">{t("rsvp.deadline")}</p>
        </MotionReveal>
        <MotionReveal delay={0.15}>
          <p className="mt-8 text-foreground/75 max-w-[60ch] leading-relaxed text-lg">
            {editMode ? (t("rsvp.editIntro") || "Your reply is prefilled below. Change anything, then hit save.") : t("rsvp.intro")}
          </p>
        </MotionReveal>

        {/* Already replied? Name lookup — only shows when not in edit mode */}
        {!editMode && (
          <MotionReveal delay={0.2}>
            <div className="mt-8 inline-flex items-start gap-3 rounded-lg border border-border bg-foreground/[0.02] p-3 sm:p-4">
              <Search className="w-4 h-4 mt-0.5 text-foreground/50 shrink-0" />
              <div className="min-w-0">
                {!showLookup ? (
                  <p className="text-sm text-foreground/75">
                    {t("rsvp.alreadyReplied") || "Already replied?"}{" "}
                    <button
                      type="button"
                      onClick={() => setShowLookup(true)}
                      className="underline underline-offset-4 hover:text-accent"
                      data-testid="rsvp-find-my-reply"
                    >
                      {t("rsvp.findMyReply") || "Find my reply"}
                    </button>
                  </p>
                ) : (
                  <form
                    className="flex flex-col gap-2"
                    onSubmit={(e) => { e.preventDefault(); onFindMyReply(); }}
                    data-testid="rsvp-lookup-form"
                  >
                    <p className="text-[12px] text-foreground/70 leading-snug">
                      {t("rsvp.lookupIntro") || "Type the full name you used the first time — we'll load your answer so you can change anything."}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        placeholder={t("rsvp.lookupPlaceholder") || "e.g. Anna Kowalska"}
                        value={lookupName}
                        onChange={(e) => setLookupName(e.target.value)}
                        autoFocus
                        className="h-10 min-w-[220px]"
                        data-testid="rsvp-lookup-input"
                      />
                      <Button
                        type="submit"
                        disabled={lookupBusy || !lookupName.trim()}
                        data-testid="rsvp-lookup-submit"
                        className="h-10 px-4 rounded-lg bg-foreground text-background hover:bg-foreground/90 tracking-[0.14em] uppercase text-[11px]"
                      >
                        {lookupBusy ? "…" : (t("rsvp.lookupCta") || "Find my reply")}
                      </Button>
                      <button
                        type="button"
                        onClick={() => { setShowLookup(false); setLookupName(""); }}
                        className="text-[11px] uppercase tracking-[0.14em] text-foreground/50 hover:text-foreground px-2 self-start sm:self-center"
                      >
                        {t("common.cancel") || "Cancel"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </MotionReveal>
        )}

        {/* Edit mode badge */}
        {editMode && (
          <MotionReveal delay={0.2}>
            <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-accent/10 border border-accent/40 px-3 py-1.5">
              <PencilLine className="w-3.5 h-3.5 text-accent" />
              <span className="text-[11px] tracking-[0.14em] uppercase text-accent">
                {t("rsvp.editBadge") || "Editing your existing reply"}
              </span>
              <button
                type="button"
                onClick={cancelEdit}
                className="text-[11px] tracking-[0.14em] uppercase text-foreground/50 hover:text-foreground ml-2"
                data-testid="rsvp-cancel-edit"
              >
                {t("common.cancel") || "Cancel"}
              </button>
            </div>
          </MotionReveal>
        )}
      </section>

      <section className="mx-auto max-w-[760px] px-5 sm:px-8 mt-14 sm:mt-20 pb-24">
        <form onSubmit={onSubmit} className="space-y-8" data-testid="rsvp-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="editorial-kicker" htmlFor="name">{t("rsvp.fields.name")}</Label>
              <Input
                id="name"
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                data-testid="rsvp-form-name-input"
                className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
              />
              {editMode && (
                <p className="editorial-kicker text-foreground/50">
                  {t("rsvp.editNameHint") || "Keep the same name — it's how we find your reply."}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="editorial-kicker" htmlFor="email">{t("rsvp.fields.email")}</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                data-testid="rsvp-form-email-input"
                className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label className="editorial-kicker">{t("rsvp.fields.attending")}</Label>
              <Select value={form.attending} onValueChange={(v) => update("attending", v)}>
                <SelectTrigger data-testid="rsvp-form-attendance-select" className="h-12 rounded-none border-0 border-b border-border bg-transparent focus:ring-0 focus:border-accent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">{t("rsvp.fields.attendingYes")}</SelectItem>
                  <SelectItem value="no">{t("rsvp.fields.attendingNo")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="editorial-kicker" htmlFor="guests">{t("rsvp.fields.guests")}</Label>
              <Input
                id="guests"
                type="number"
                min={1}
                max={10}
                value={form.guest_count}
                onChange={(e) => update("guest_count", e.target.value)}
                data-testid="rsvp-form-guests-input"
                className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
              />
            </div>
            <div className="space-y-2">
              <Label className="editorial-kicker" htmlFor="kids">{t("rsvp.fields.kids")}</Label>
              <Input
                id="kids"
                type="number"
                min={0}
                max={10}
                value={form.kid_count}
                onChange={(e) => update("kid_count", e.target.value)}
                data-testid="rsvp-form-kids-input"
                className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
              />
              <p className="editorial-kicker text-foreground/50">
                {t("rsvp.fields.kidsHint")}
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="editorial-kicker">{t("rsvp.fields.accommodation")}</Label>
              <Select value={form.accommodation} onValueChange={(v) => update("accommodation", v)}>
                <SelectTrigger data-testid="rsvp-form-accommodation-select" className="h-12 rounded-none border-0 border-b border-border bg-transparent focus:ring-0 focus:border-accent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="need">{t("rsvp.fields.accommodationNeed")}</SelectItem>
                  <SelectItem value="own">{t("rsvp.fields.accommodationOwn")}</SelectItem>
                  <SelectItem value="no">{t("rsvp.fields.accommodationNo")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="editorial-kicker" htmlFor="allergies">{t("rsvp.fields.allergies")}</Label>
              <Input
                id="allergies"
                value={form.allergies}
                onChange={(e) => update("allergies", e.target.value)}
                data-testid="rsvp-form-allergies-input"
                className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="editorial-kicker" htmlFor="arrival">{t("rsvp.fields.arrivalDate")}</Label>
              <Input
                id="arrival"
                type="date"
                min="2027-05-01"
                max="2027-06-30"
                value={form.arrival_date}
                onChange={(e) => update("arrival_date", e.target.value)}
                data-testid="rsvp-form-arrival-input"
                className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
              />
            </div>
            <div className="space-y-2">
              <Label className="editorial-kicker" htmlFor="departure">{t("rsvp.fields.departureDate")}</Label>
              <Input
                id="departure"
                type="date"
                min={form.arrival_date || "2027-05-01"}
                max="2027-07-15"
                value={form.departure_date}
                onChange={(e) => update("departure_date", e.target.value)}
                data-testid="rsvp-form-departure-input"
                className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
              />
            </div>
            <p className="editorial-kicker sm:col-span-2 text-foreground/50">
              {t("rsvp.fields.arrivalHint")}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="editorial-kicker" htmlFor="song">{t("rsvp.fields.song")}</Label>
            <Input
              id="song"
              value={form.song_request}
              onChange={(e) => update("song_request", e.target.value)}
              placeholder={t("rsvp.fields.songPlaceholder")}
              data-testid="rsvp-form-song-input"
              className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
            />
            <p className="editorial-kicker text-foreground/50">
              {t("rsvp.fields.songHint")}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="editorial-kicker" htmlFor="message">{t("rsvp.fields.message")}</Label>
            <Textarea
              id="message"
              rows={5}
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              data-testid="rsvp-form-message-input"
              className="border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent resize-none"
            />
          </div>

          {/* Menu pre-order */}
          <div className="pt-6" data-testid="rsvp-menu-section">
            <MenuPreorder counts={menuCounts} onChange={setMenuCounts} />
          </div>

          <div className="pt-4 flex flex-wrap items-center gap-3">
            <Button
              type="submit"
              disabled={busy}
              data-testid="rsvp-form-submit-button"
              className="h-12 px-8 rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-[0.22em] uppercase text-[12px]"
            >
              {busy ? t("rsvp.sending") : editMode ? (t("rsvp.saveEdit") || "Save changes") : t("rsvp.submit")}
            </Button>
            {editMode && (
              <button
                type="button"
                onClick={cancelEdit}
                className="text-[11px] tracking-[0.14em] uppercase text-foreground/50 hover:text-foreground"
                data-testid="rsvp-cancel-edit-bottom"
              >
                {t("common.cancel") || "Cancel"}
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
};

export default Rsvp;
